import { useState } from 'react';
import { Notice, Pill, Stat, Why, ago } from '../components/ui.jsx';
import { record as store, summarise } from '../lib/record.js';

export default function Record({ record, identity }) {
  const { total, locked } = summarise(record.readings);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const download = () => {
    const blob = new Blob([store.exportBlob()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'omnialera-ori-record.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Deferred, as pulsecard.js already does: revoking in the same tick as the
    // click races the browser and produces no file and no error in Firefox and
    // WebKit — the person clicks Export and simply nothing happens.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const erase = async () => {
    setBusy(true);
    setError(null);
    try {
      await store.eraseEverything();
      setConfirming(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="vh">
        <p className="eyebrow">Your record</p>
        <h1>Kept here, not on our servers</h1>
        <p>
          Everything Ori knows about you lives in this browser. We have no copy. That is not a limitation to be
          engineered away later — it is the architecture, and it is what makes the sharing controls mean anything.
        </p>
      </div>

      <div className="trio" style={{ maxWidth: '640px' }}>
        <Stat label="Readings" value={total} />
        <Stat label="Successful" value={locked} />
        <Stat label="Credits" value={record.credits} />
      </div>

      {identity ? (
        <div className="card" style={{ marginTop: 'var(--s2)' }}>
          <p className="eyebrow">This device</p>
          <p className="mono" style={{ fontSize: '0.88rem', color: 'var(--ink-soft)', wordBreak: 'break-all', margin: '8px 0 0' }}>
            {identity.deviceId}
          </p>
          <p style={{ fontSize: '0.92rem', color: 'var(--muted)', margin: '10px 0 0', lineHeight: 1.6 }}>
            A name your browser made up for itself, derived from a key it generated and never sent anywhere. It is not
            linked to you, and the private half cannot be read even by this page — which is what makes a withdrawal
            impossible for anyone else to forge.
          </p>
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 'var(--s2)' }}>
        <div className="lst">
          {record.readings.length === 0 ? (
            <p style={{ color: 'var(--faint)', margin: 0 }}>Nothing recorded yet. Take a reading and it appears here.</p>
          ) : (
            [...record.readings]
              .reverse()
              .slice(0, 25)
              .map((r) => (
                <div className="it" key={r.observedAt + r.seconds}>
                  <span className="when">{ago(r.observedAt)}</span>
                  <div className="main">
                    <b>{r.locked ? `${Math.round(r.heartRateBpm)} bpm` : 'could not read'}</b>
                    <span>
                      Tone {r.monkTone} ·{' '}
                      {r.locked ? `found in ${r.seconds.toFixed(1)}s` : `gave up after ${Math.round(r.seconds)}s`} ·{' '}
                      {r.source ?? 'simulated'}
                      {r.shared ? ' · shared' : ''}
                    </span>
                  </div>
                  <Pill kind={r.locked ? 'ok' : 'bad'}>{r.locked ? 'ok' : 'none'}</Pill>
                </div>
              ))
          )}
        </div>

        <div className="btnrow" style={{ marginTop: 'var(--s3)' }}>
          <button type="button" className="btn ghost" onClick={download} disabled={!record.readings.length}>
            Export my record
          </button>
          <button type="button" className="btn ghost" onClick={() => store.clearReadings()} disabled={!record.readings.length}>
            Clear readings
          </button>
          <button type="button" className="btn danger" onClick={() => setConfirming(true)} disabled={busy}>
            Erase everything
          </button>
        </div>
      </div>

      {confirming ? (
        <div style={{ marginTop: 'var(--s2)' }}>
          <Notice kind="bad" title="Erase everything">
            This withdraws every consent you have given, then destroys the key this device signs with. Anything already
            shared is removed from future research extracts, and the partners holding past extracts are told. This
            cannot be undone, and afterwards this browser starts over as a stranger.
          </Notice>
          <div className="btnrow">
            <button type="button" className="btn danger" onClick={erase} disabled={busy}>
              {busy ? 'Withdrawing…' : 'Yes, erase everything'}
            </button>
            <button type="button" className="btn ghost" onClick={() => setConfirming(false)} disabled={busy}>
              Keep my record
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div style={{ marginTop: 'var(--s2)' }}>
          <Notice kind="warn" title="Nothing was erased">
            {error}
          </Notice>
        </div>
      ) : null}

      <Why>
        <strong>A competitor can copy the scan in a week.</strong> Copying a consent architecture that people actually
        trust takes a reputation, and reputations are slow. A record you cannot leave with was never yours.
      </Why>
    </section>
  );
}
